import { Navigate, Outlet } from "react-router-dom";
import {
  Boxes,
  ClipboardList,
  FolderOpen,
  HandCoins,
  History,
  Landmark,
  LayoutDashboard,
  LifeBuoy,
  LogIn,
  MapPin,
  Truck,
  Megaphone,
  Network,
  Package,
  Percent,
  Receipt,
  ScrollText,
  Store,
  TicketPercent,
  UserPlus,
  Users,
} from "lucide-react";
import { AppShell, type NavMap } from "@/shared/ui/layout";
import { useAuth } from "@/features/auth/auth.context";

/**
 * La navegación de la aplicación. DATOS, no JSX.
 *
 * De esta única lista salen tres cosas: los enlaces de la barra lateral,
 * los del cajón móvil y el TÍTULO de la barra superior. Dos listas que
 * dicen lo mismo divergen a la primera pantalla nueva, y el síntoma es
 * una barra superior que nombra una pantalla en la que ya no estás.
 *
 * Las etiquetas van en español porque el bundle de i18n usa claves
 * naturales (`keySeparator: false`): la etiqueta ES la clave, así que no
 * hay una lista de claves separada del texto que se lee.
 *
 * ── El agrupado ──────────────────────────────────────────────────────
 * "Administración" es el trabajo real del panel; hoy solo hay productos,
 * pero es la sección que va a crecer. "Sistema" son las pantallas de
 * herramienta: comprobar que el backend responde y consultar el design
 * system — se usan a diario mientras se construye y no son trabajo de
 * negocio.
 *
 * "Pantallas públicas" es la que había que separar sí o sí: la tienda y
 * las de acceso viven FUERA de este armazón —traen su propio cromo y su
 * propio fondo— y están enlazadas solo para poder revisarlas sin escribir
 * la URL a mano. Mezcladas con las demás parecerían secciones del panel,
 * y pulsar "Acceso" sacaría a alguien de la aplicación sin avisar. Por
 * eso van al final, separadas por un filete, con su nota y con la flecha
 * de "esto sale de aquí" en cada enlace.
 */
export const APP_NAV: NavMap = [
  {
    label: "Administración",
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
      { to: "/admin/products", label: "Productos", icon: Package },
      /*
       * Paquetes, en el menú (Anahí, 29-ago: "aquí no veo a todos los
       * paquetes"). La pantalla existía y funcionaba —el servidor
       * devuelve los 14— pero no había forma de LLEGAR a ella sin
       * escribir la URL, así que se buscaban en Productos, que solo
       * lista productos sueltos. Una pantalla sin entrada en el menú es
       * una pantalla que no existe para quien la usa.
       */
      { to: "/admin/packages", label: "Paquetes", icon: Boxes },
      /* Registros (Anahí, 14-sep-2026): el alta de un socio con su pedido y su invitación. */
      { to: "/admin/registros", label: "Registros", icon: UserPlus },
      /*
       * Órdenes y Compras están en el sidebar ANTES de tener datos
       * (Anahí, 26-ago): sus pantallas enseñan un estado vacío honesto
       * hasta que exista el checkout que las llene.
       */
      { to: "/admin/orders", label: "Órdenes", icon: ClipboardList },
      { to: "/admin/purchases", label: "Compras", icon: Receipt },
      { to: "/admin/coupons", label: "Cupones", icon: TicketPercent },
      { to: "/admin/sedes", label: "Sedes", icon: MapPin },
      { to: "/admin/envios", label: "Envíos", icon: Truck },
      { to: "/admin/users", label: "Usuarios", icon: Users },
    ],
  },
  /*
   * La red y su dinero, juntos: el árbol, el libro de lo que se debe y
   * el plan que decide los porcentajes.
   */
  {
    label: "Red y comisiones",
    items: [
      { to: "/admin/network", label: "Red", icon: Network },
      { to: "/admin/commissions", label: "Comisiones", icon: HandCoins },
      { to: "/admin/commission-params", label: "Plan de comisiones", icon: Percent },
    ],
  },
  {
    label: "Franquicias",
    items: [
      // La configuración es una PESTAÑA dentro (Anahí, 27-ago), no otra
      // entrada del menú.
      { to: "/admin/franchises", label: "Franquicias", icon: Store },
    ],
  },
  /*
   * Lo que el sitio DICE: el anuncio, los documentos del pie, el buzón
   * del formulario de contacto y la biblioteca de formación.
   */
  {
    label: "Contenido y soporte",
    items: [
      { to: "/admin/banner", label: "Banner", icon: Megaphone },
      /*
       * La cuenta a la que transfiere quien elige transferencia. Va en
       * "Contenido y ajustes" porque es eso: un dato editable de la
       * casa, no una operación.
       */
      { to: "/admin/cuenta-bancaria", label: "Cuenta bancaria", icon: Landmark },
      { to: "/admin/legal-docs", label: "Documentos legales", icon: ScrollText },
      { to: "/admin/support", label: "Soporte", icon: LifeBuoy },
      { to: "/admin/resources", label: "Recursos", icon: FolderOpen },
    ],
  },
  /*
   * La Bitácora (Anahí, 2-oct-2026): quién pidió qué, qué se le contestó,
   * qué se está rompiendo y qué IP están vetadas. Sección propia porque
   * no es negocio ni contenido: es vigilar el sistema.
   */
  {
    label: "Sistema",
    items: [{ to: "/admin/bitacora", label: "Bitácora", icon: History }],
  },
  {
    label: "Pantallas públicas",
    note: "Viven fuera del panel y traen su propio cromo.",
    external: true,
    items: [
      { to: "/shop", label: "Tienda", icon: Store },
      { to: "/paquetes", label: "Paquetes públicos", icon: Boxes },
      { to: "/login", label: "Acceso", icon: LogIn },
      { to: "/register", label: "Crear cuenta", icon: UserPlus },
    ],
  },
];

export function RootLayout() {
  const { isAuthenticated, user } = useAuth();

  /*
   * Sin sesión NO hay panel (Anahí, 26-ago): ni el armazón, ni la barra
   * lateral con las secciones, ni el aviso de "entra para continuar" en
   * medio del cromo de administración. A la tienda, que es la cara
   * pública.
   *
   * Esto cubre los dos caminos con una sola regla: cerrar sesión DESDE
   * el panel (el usuario pasa a null, el siguiente render redirige) y
   * llegar por URL directa sin sesión. No hay parpadeo: el proveedor de
   * auth enseña su velo mientras resuelve, así que este componente solo
   * se monta con el estado ya decidido.
   *
   * Y el ROL también se comprueba AQUÍ (Anahí, 27-ago: "esa pantalla ya
   * no debe mostrarse"): desde que cada rol tiene SU panel, un cliente
   * en /admin no recibe el armazón administrativo con un aviso adentro
   * — va derecho a su casa: su panel de socio, o su negocio si es
   * franquiciatario. `AdminGuard` sigue en cada pantalla como segunda
   * llave; los DATOS siempre estuvieron detrás del rol en el servidor.
   */
  if (!isAuthenticated) return <Navigate to="/shop" replace />;
  if (user && user.role !== "super_admin" && user.role !== "admin") {
    return <Navigate to={user.franchiseeSince ? "/mi-negocio" : "/socio"} replace />;
  }

  return (
    <AppShell nav={APP_NAV}>
      <Outlet />
    </AppShell>
  );
}
